import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { Property } from 'csstype'
import chroma from 'chroma-js'
import type { Color, Scale } from 'chroma-js'

const COLOR_DOMAIN = [1,6,12]
const FOREGROUND_INDEX = 3;
const BACKGROUND_INDEX = 10;

@customElement('ep-avatar')
export class EPAvatar extends LitElement {

  @property({ type: Number, reflect: true })
  size: number = 32;

  @property({ type: Boolean, reflect: true })
  active: boolean = false;

  @property({ reflect: true })
  initials?: string;

  get missingInitials(): boolean {
    return this.initials === undefined || `${this.initials}`.length === 0;
  }

  @property({ reflect: true })
  color: Property.Color = 'slategray';

  @property({ attribute: 'background-color', reflect: true })
  providedBackgroundColor?: Property.Color = undefined;

  @property({ reflect: true })
  image?: string;

  get fontSizeVariable(): string {
    if (this.size < 28) return '--font-size-100';
    if (this.size < 32) return '--font-size-200';
    if (this.size < 48) return '--font-size-300';
    if (this.size < 64) return '--font-size-400';
    if (this.size < 120) return '--font-size-500';
    return '--font-size-600';
  }

  get colorScale(): Scale {
    const primary = this.primary;
    // TODO: play with boundaries so that colors look the best
    const light = chroma('#fff');
    //const light = primary.brighten(3.0).desaturate(1.2);
    const dark = chroma('#000');
    //const dark = primary.darken(3.0);
    return chroma.scale([dark, primary, light])
      //.correctLightness()
      .domain(COLOR_DOMAIN);
  }

  get primary() {
    try {
      return chroma(this.color);
    }
    catch {
      return chroma('#000');
    }
  }

  get chroma() {
    return chroma(this.color);
  }

  get chromaBG() {
    return chroma(this.backgroundColor);
  }

  get foregroundColor(): Property.Color {
    if (this.providedBackgroundColor !== undefined) return this.color;
    // const temp = this.primary;
    // return temp.luminance(0.05).css();
    const fg = this.colorScale(FOREGROUND_INDEX);
    return fg.css();
  }

  get backgroundColor(): Property.Color {
    if (this.providedBackgroundColor !== undefined) return this.providedBackgroundColor;
    // const temp = this.primary;
    // return temp.luminance(0.6).css(); // .set('hsl.s', '0.53').set('hsl.l', '0.79')
    const bg = this.colorScale(BACKGROUND_INDEX);
    return bg.css();
  }

  testColorSpread() {
    let primary: Color;
    try {
      primary = chroma(this.color);
    }
    catch {
      primary = chroma('#000');
    }
    const scale = this.colorScale;
    for(let i = COLOR_DOMAIN[0]; i <= COLOR_DOMAIN[COLOR_DOMAIN.length-1]; i++) {
      const t = scale(i);
      let note = '';
      if (primary.css() === t.css()) note += '(Primary)';
      if (i === FOREGROUND_INDEX) note += '(Foreground)';
      if (i === BACKGROUND_INDEX) note += '(Background)';
      console.log(`%cColor ${i} (${t.hex()}) ${note}`, `background: ${t.css()};`)
    }
  }

  render() {
    // window.chroma = chroma;
    // this.testColorSpread();
    return html`
      <span class="avatar" ?data-active=${this.active} style=${`--font-size: var(${this.fontSizeVariable}); --size: ${this.size}px; --foreground-color: ${this.foregroundColor}; --background-color: ${this.backgroundColor};`}>
        <span class="initials">${this.initials}</span>
        <span class="icon" style=${!this.missingInitials ? 'display: none;' : ''}>
          <svg fill="currentColor" width="1.0em" height="1.0em" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM7 6a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm-2 5a2 2 0 0 0-2 2c0 1.7.83 2.97 2.13 3.8A9.14 9.14 0 0 0 10 18c1.85 0 3.58-.39 4.87-1.2A4.35 4.35 0 0 0 17 13a2 2 0 0 0-2-2H5Zm-1 2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1c0 1.3-.62 2.28-1.67 2.95A8.16 8.16 0 0 1 10 17a8.16 8.16 0 0 1-4.33-1.05A3.36 3.36 0 0 1 4 13Z" fill="currentColor"></path>
          </svg>
          
        </span>
        ${
          this.image !== undefined
          ? html`
          <img role="presentation" src=${this.image} />
          `
          : ''
        }
      </span>
    `
  }

  static styles = css`
    :host {
      display: inline-block;
      --font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
      --font-size: var(--font-size-300);
      --font-size-100: 10px;
      --font-size-200: 12px;
      --font-size-300: 14px;
      --font-size-400: 16px;
      --font-size-500: 20px;
      --font-size-600: 24px;
      --avatar-ring-width: 2px;
      --avatar-ring-color: #0f6cbd;
      --foreground-color: #616161;
      --background-color: #e6e6e6;
      /* shade50: '#200205',
      shade40: '#3b0509',
      shade30: '#6e0811',
      shade20: '#960b18',
      shade10: '#b10e1c',
      primary: '#c50f1f',
      tint10: '#cc2635',
      tint20: '#d33f4c',
      tint30: '#dc626d',
      tint40: '#eeacb2',
      tint50: '#f6d1d5',
      tint60: '#fdf3f4', */
    }

    .avatar[data-active]::before {
      content: "";
    }

    .avatar::before {
      color: var(--avatar-ring-color);
      border-style: solid;
      border-width: var(--avatar-ring-width);
    }

    .avatar::before, .avatar::after {
      position: absolute;
      inset: 0px;
      z-index: -1;
      margin: calc(-2 * var(--avatar-ring-width, 0px));
      border-radius: inherit;
      transition-property: margin, opacity;
      transition-timing-function: cubic-bezier(0.8, 0, 0.1, 1), cubic-bezier(0, 0, 1, 1);
      transition-duration: 0.5s, 0.4s;
    }

    .avatar {
      transform: perspective(1px);
      display: inline-block;
      flex-shrink: 0;
      position: relative;
      vertical-align: middle;
      border-radius: 10000px;
      font-family: var(--font-family);
      font-weight: 600;
      font-size: var(--font-size); /* How to scale? */
      width: var(--size); /* Replace with size */
      height: var(--size);
    }

    .initials, .icon {
      /* background-color: #e6e6e6; */
      /* background-color: rgba(var(--color-rgb),0.25); */
      background-color: var(--background-color);
      /* color: #616161; */
      /* color: rgba(0,0,0,0.96); */
      /* color: rgb(var(--color-rgb)); */
      color: var(--foreground-color);
      position: absolute;
      box-sizing: border-box;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      line-height: 1;
      border: 1px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      user-select: none;
      border-radius: inherit;
    }

    .icon svg {
      display: inline;
      line-height: 0;
      width: calc(var(--size) * 0.625);
      height: calc(var(--size) * 0.625);
    }

    .avatar img {
      position: absolute;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      border-radius: inherit;
      object-fit: cover;
      vertical-align: top;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-avatar': EPAvatar
  }
}
